import { useState, useEffect, useCallback, lazy, Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
import { Package, ShoppingCart, Settings, LogOut, Plus, Pencil, Trash2, Download, Eye, ChevronDown, ChevronUp, X, Search, Upload, Image, Loader2, Star, Users, Globe, Sparkles, Palette, Bell, CheckSquare, Square, GripVertical, BarChart3, Menu, Database, ShieldCheck, Clock, HelpCircle, BookOpen, Mail, Webhook, Crown, Megaphone, BadgeDollarSign, Gift } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Switch } from '../components/ui/switch';
import Pagination from '../components/Pagination';

// Lazy-loaded heavy tabs — only fetched & parsed when user clicks the tab
const TwoFactorSetup = lazy(() => import('../components/TwoFactorSetup'));
const AnalyticsTab = lazy(() => import('../components/AnalyticsTab'));
const PushBroadcastPanel = lazy(() => import('../components/PushBroadcastPanel'));
const ReviewsModerationTab = lazy(() => import('../components/ReviewsModerationTab'));
const DigestConfigPanel = lazy(() => import('../components/DigestConfigPanel'));
const AmbassadorsTab = lazy(() => import('../components/AmbassadorsTab'));
const FAQTab = lazy(() => import('../components/FAQTab'));
const BlogTab = lazy(() => import('../components/BlogTab'));
const ContactInboxTab = lazy(() => import('../components/ContactInboxTab'));
const WebhooksTab = lazy(() => import('../components/WebhooksTab'));
const AmbassadorApplicationsTab = lazy(() => import('../components/AmbassadorApplicationsTab'));
const TesterKitOrdersTab = lazy(() => import('../components/TesterKitOrdersTab'));
const BulkOrdersTab = lazy(() => import('../components/BulkOrdersTab'));
const BusinessConfigTab = lazy(() => import('../components/BusinessConfigTab'));
const SimpleProductsTab = lazy(() => import('../components/SimpleProductsTab'));

// Modals + widgets — extracted from this file (was 3,400+ LOC) into per-component files.
// Loaded eagerly because they're tightly coupled to admin actions and tend to be opened
// in quick succession (no benefit from code-splitting these vs the heavy tabs above).
import ProductModal from '../components/admin/ProductModal';
import ShippingFeeSettings from '../components/admin/ShippingFeeSettings';
import CollectionModal from '../components/admin/CollectionModal';
import FeedbackModal from '../components/admin/FeedbackModal';
import CustomerModal from '../components/admin/CustomerModal';
import ScentNoteModal from '../components/admin/ScentNoteModal';
import ScentPresetModal from '../components/admin/ScentPresetModal';
import TeamMemberModal from '../components/admin/TeamMemberModal';
import AuditLogViewer from '../components/admin/AuditLogViewer';
import AuditLogTab from '../components/admin/AuditLogTab';
import StorageUsageWidget from '../components/admin/StorageUsageWidget';
import AnnouncementsTab from '../components/admin/AnnouncementsTab';
import WithdrawalsTab from '../components/admin/WithdrawalsTab';
import PrizeClaimsTab from '../components/admin/PrizeClaimsTab';
import CoachingTipsTab from '../components/admin/CoachingTipsTab';
import HealthStatusBadge from '../components/admin/HealthStatusBadge';
import DualRoleSwitcher from '../components/admin/DualRoleSwitcher';
import CapiHealthCard from '../components/admin/CapiHealthCard';

const TabFallback = () => <div className="text-white/40 text-sm text-center py-12">Loading…</div>;

const API = `${window.location.origin}/api`;

function getAuthHeaders() {
  const token = localStorage.getItem('admin_token');
  return { Authorization: `Bearer ${token}` };
}

import { getOrderStatusColor as getStatusColor, formatOrderStatusLabel as formatStatusLabel } from '../utils/orderStatus';

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('products');
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [settings, setSettings] = useState({ cod_enabled: false });
  const [loading, setLoading] = useState(true);
  const [showProductModal, setShowProductModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [orderFilter, setOrderFilter] = useState('');
  const [orderSearch, setOrderSearch] = useState('');
  const [productSearch, setProductSearch] = useState('');
  const [collections, setCollections] = useState([]);
  const [showCollectionModal, setShowCollectionModal] = useState(false);
  const [editingCollection, setEditingCollection] = useState(null);
  const [feedbackList, setFeedbackList] = useState([]);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [editingFeedback, setEditingFeedback] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [customerSearch, setCustomerSearch] = useState('');
  const [notifications, setNotifications] = useState({ new_orders: 0, pending_feedback: 0, pending_custom_scents: 0, new_messages: 0, new_ambassador_apps: 0 });
  const [categoryTemplates, setCategoryTemplates] = useState([]);

  // Load category templates so we can render a tab per category dynamically
  useEffect(() => {
    axios.get(`${API}/category-templates`)
      .then(r => setCategoryTemplates(r.data?.templates || []))
      .catch(() => {});
  }, []);
  const [customScentOrders, setCustomScentOrders] = useState([]);
  const [scentNotes, setScentNotes] = useState([]);
  const [scentPresets, setScentPresets] = useState([]);
  const [showScentNoteModal, setShowScentNoteModal] = useState(false);
  const [editingScentNote, setEditingScentNote] = useState(null);
  const [showScentPresetModal, setShowScentPresetModal] = useState(false);
  const [editingScentPreset, setEditingScentPreset] = useState(null);
  const [expandedCustomOrder, setExpandedCustomOrder] = useState(null);
  const [customScentTab, setCustomScentTab] = useState('orders');
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [showBulkEdit, setShowBulkEdit] = useState(false);
  const [bulkFields, setBulkFields] = useState({});
  const [ogDragging, setOgDragging] = useState(false);
  const [ogUploading, setOgUploading] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);
  const [showTeamModal, setShowTeamModal] = useState(false);
  const [editingTeamMember, setEditingTeamMember] = useState(null);

  // Sidebar state (collapsible on mobile)
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Pagination state for big tables (client-side over server-loaded batch)
  const [ordersPage, setOrdersPage] = useState(1);
  const [customersPage, setCustomersPage] = useState(1);
  const [productsPage, setProductsPage] = useState(1);
  const PAGE_SIZE = 25;

  const fetchData = useCallback(async () => {
    try {
      const headers = getAuthHeaders();
      const [prodRes, ordRes, setRes, colRes, fbRes, custRes, notifRes, csOrdersRes, notesRes, presetsRes, teamRes] = await Promise.all([
        axios.get(`${API}/products`, { headers }),
        axios.get(`${API}/admin/orders`, { headers }),
        axios.get(`${API}/settings`),
        axios.get(`${API}/admin/collections`, { headers }),
        axios.get(`${API}/admin/feedback`, { headers }),
        axios.get(`${API}/admin/customers`, { headers }),
        axios.get(`${API}/admin/notifications`, { headers }),
        axios.get(`${API}/admin/custom-scent/orders`, { headers }),
        axios.get(`${API}/admin/scent-notes`, { headers }),
        axios.get(`${API}/admin/scent-presets`, { headers }),
        axios.get(`${API}/admin/team`, { headers }),
      ]);
      setProducts(prodRes.data);
      setOrders(ordRes.data);
      setSettings(setRes.data);
      setCollections(colRes.data);
      setFeedbackList(fbRes.data);
      setCustomers(custRes.data);
      setNotifications(notifRes.data);
      // Inbox count fetched in background — non-blocking
      axios.get(`${API}/admin/contact-messages`, { headers }).then(r => {
        setNotifications(n => ({ ...n, new_messages: r.data.new_count || 0 }));
      }).catch(() => {});
      // Ambassador applications count
      axios.get(`${API}/admin/ambassador-applications`, { headers }).then(r => {
        setNotifications(n => ({ ...n, new_ambassador_apps: r.data.new_count || 0 }));
      }).catch(() => {});
      // Tester kit pending payments count
      axios.get(`${API}/admin/tester-kit-orders?status=paid_pending_approval`, { headers }).then(r => {
        setNotifications(n => ({ ...n, pending_tester_kit: r.data.pending_count || 0 }));
      }).catch(() => {});
      // Withdrawal queue count (pending only — drives the sidebar badge)
      axios.get(`${API}/admin/withdrawals?status=pending`, { headers }).then(r => {
        setNotifications(n => ({ ...n, pending_withdrawals: (r.data.items || []).length }));
      }).catch(() => {});
      setCustomScentOrders(csOrdersRes.data);
      setScentNotes(notesRes.data);
      setScentPresets(presetsRes.data);
      setTeamMembers(teamRes.data);
      setLoading(false);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem('admin_token');
        navigate('/admin');
      }
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) { navigate('/admin'); return; }
    fetchData();
    // Poll notifications every 30s
    const interval = setInterval(async () => {
      try {
        const headers = getAuthHeaders();
        const [notifRes, msgRes, ambRes, tkRes, wdRes, pcRes] = await Promise.all([
          axios.get(`${API}/admin/notifications`, { headers }),
          axios.get(`${API}/admin/contact-messages`, { headers }).catch(() => ({ data: { new_count: 0 } })),
          axios.get(`${API}/admin/ambassador-applications`, { headers }).catch(() => ({ data: { new_count: 0 } })),
          axios.get(`${API}/admin/tester-kit-orders?status=paid_pending_approval`, { headers }).catch(() => ({ data: { pending_count: 0 } })),
          axios.get(`${API}/admin/withdrawals?status=pending`, { headers }).catch(() => ({ data: { items: [] } })),
          axios.get(`${API}/admin/prize-claims?status=pending`, { headers }).catch(() => ({ data: [] })),
        ]);
        setNotifications({
          ...notifRes.data,
          new_messages: msgRes.data.new_count || 0,
          new_ambassador_apps: ambRes.data.new_count || 0,
          pending_tester_kit: tkRes.data.pending_count || 0,
          pending_withdrawals: (wdRes.data.items || []).length,
          pending_prize_claims: Array.isArray(pcRes.data) ? pcRes.data.length : 0,
        });
      } catch { /* silent */ }
    }, 30000);
    return () => clearInterval(interval);
  }, [navigate, fetchData]);

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    axios.post(`${API}/auth/logout`, {});
    navigate('/admin');
  };

  const handleDeleteProduct = async (id) => {
    if (!window.confirm('Delete this product?')) return;
    try {
      await axios.delete(`${API}/admin/products/${id}`, { headers: getAuthHeaders() });
      toast.success('Product deleted');
      fetchData();
    } catch { toast.error('Failed to delete'); }
  };

  const handleStatusUpdate = async (orderId, status) => {
    try {
      await axios.put(`${API}/admin/orders/${orderId}`, { status }, { headers: getAuthHeaders() });
      toast.success(`Status updated to ${status}`);
      fetchData();
    } catch { toast.error('Failed to update'); }
  };

  const handleTrackingUpdate = async (orderId, tracking) => {
    try {
      await axios.put(`${API}/admin/orders/${orderId}`, { status: orders.find(o => o.id === orderId)?.status || 'Shipped', tracking_number: tracking }, { headers: getAuthHeaders() });
      toast.success('Tracking updated');
      fetchData();
    } catch { toast.error('Failed to update'); }
  };

  const handleCODToggle = async (enabled) => {
    try {
      await axios.put(`${API}/admin/settings`, { ...settings, cod_enabled: enabled, key: 'site_settings' }, { headers: getAuthHeaders() });
      setSettings(prev => ({ ...prev, cod_enabled: enabled }));
      toast.success(enabled ? 'COD enabled' : 'COD disabled');
    } catch { toast.error('Failed to update'); }
  };

  const handleExportCSV = () => {
    const token = localStorage.getItem('admin_token');
    window.open(`${API}/admin/orders/export/csv?auth=${token}`, '_blank');
  };

  const filteredOrders = orders.filter(o => {
    if (orderFilter && o.status !== orderFilter) return false;
    if (orderSearch) {
      const s = orderSearch.toLowerCase();
      return o.order_number?.toLowerCase().includes(s) || o.customer_name?.toLowerCase().includes(s) || o.phone?.includes(s);
    }
    return true;
  });

  // Slice filtered lists for current page
  const ordersTotal = filteredOrders.length;
  const ordersPages = Math.max(1, Math.ceil(ordersTotal / PAGE_SIZE));
  const pagedOrders = filteredOrders.slice((ordersPage - 1) * PAGE_SIZE, ordersPage * PAGE_SIZE);

  // Reset page when filters change
  useEffect(() => { setOrdersPage(1); }, [orderSearch, orderFilter]);
  useEffect(() => { setCustomersPage(1); }, [customerSearch]);
  useEffect(() => { setProductsPage(1); }, [productSearch]);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><p className="text-white/40">Loading...</p></div>;

  // Build per-category catalog tabs from the category templates config.
  // Perfume keeps the existing rich Products UI (with AI scent generation, notes, etc.);
  // every other category uses the lightweight SimpleProductsTab.
  const ICON_MAP = { Sparkles: Sparkles, Bike: Crown, Car: ShoppingCart, Home: Image, Droplet: Palette, Package: Package };
  const catalogItems = [
    // Default fallback when templates haven't loaded yet
    ...(categoryTemplates.length === 0 ? [{ key: 'products', label: 'Perfume', icon: Package }] : []),
    // One sidebar entry per category template
    ...categoryTemplates.map(t => ({
      key: t.slug === 'perfume' ? 'products' : `cat:${t.slug}`,
      label: t.label,
      icon: ICON_MAP[t.icon] || Package,
      _template: t,
    })),
    { key: 'collections', label: 'Collections', icon: Image },
    { key: 'custom_scent', label: 'Custom Scent', icon: Palette, badge: notifications.pending_custom_scents },
  ];

  // Grouped sidebar navigation — friendlier + more scannable than a flat row of tabs
  const NAV_GROUPS = [
    {
      label: 'Overview',
      items: [
        { key: 'analytics', label: 'Analytics', icon: BarChart3 },
      ],
    },
    {
      label: 'Catalog',
      items: catalogItems,
    },
    {
      label: 'Sales',
      items: [
        { key: 'orders', label: 'Orders', icon: ShoppingCart, badge: notifications.new_orders },
        { key: 'customers', label: 'Customers', icon: Users },
        { key: 'ambassadors', label: 'Ambassadors', icon: Users },
      ],
    },
    {
      label: 'Engagement',
      items: [
        { key: 'inbox', label: 'Inbox', icon: Mail, badge: notifications.new_messages },
        { key: 'announcements', label: 'Announcements', icon: Megaphone },
        { key: 'ambassador_apps', label: 'Ambassador Apps', icon: Crown, badge: notifications.new_ambassador_apps },
        { key: 'tester_kit_orders', label: 'Tester Kit Payments', icon: ShieldCheck, badge: notifications.pending_tester_kit },
        { key: 'withdrawals', label: 'Withdrawals', icon: BadgeDollarSign, badge: notifications.pending_withdrawals },
        { key: 'prize_claims', label: 'Prize Claims', icon: Gift, badge: notifications.pending_prize_claims },
        { key: 'bulk_orders', label: 'Bulk Orders', icon: Upload },
        { key: 'reviews', label: 'Reviews', icon: Star },
        { key: 'feedback', label: 'Feedback', icon: Star, badge: notifications.pending_feedback },
        { key: 'team', label: 'Team', icon: Users },
      ],
    },
    {
      label: 'Content',
      items: [
        { key: 'faq', label: 'FAQs', icon: HelpCircle },
        { key: 'blog', label: 'Blog', icon: BookOpen },
        { key: 'coaching_tips', label: "Coach's Tips", icon: Sparkles },
      ],
    },
    {
      label: 'Configuration',
      items: [
        { key: 'business_config', label: 'Pricing & Program', icon: ShieldCheck },
        { key: 'og_meta', label: 'Default Share Preview', icon: Globe },
        { key: 'webhooks', label: 'Webhooks', icon: Webhook },
        { key: 'audit', label: 'Audit Log', icon: Eye },
        { key: 'settings', label: 'Settings', icon: Settings },
      ],
    },
  ];

  const totalNotif = notifications.new_orders + notifications.pending_feedback + notifications.pending_custom_scents + (notifications.new_messages || 0) + (notifications.new_ambassador_apps || 0);
  const activeLabel = NAV_GROUPS.flatMap(g => g.items).find(i => i.key === tab)?.label || 'Dashboard';

  const SidebarNav = () => (
    <nav className="flex flex-col gap-5 py-4" data-testid="admin-sidebar-nav">
      {NAV_GROUPS.map(group => (
        <div key={group.label} className="px-3">
          <p className="text-white/30 text-[10px] uppercase tracking-[0.18em] font-sans mb-1.5 px-2">{group.label}</p>
          <div className="flex flex-col">
            {group.items.map(item => {
              const Icon = item.icon;
              const active = tab === item.key;
              return (
                <button
                  key={item.key}
                  onClick={() => { setTab(item.key); setSidebarOpen(false); }}
                  data-testid={`tab-${item.key}`}
                  className={`relative flex items-center gap-2.5 px-2.5 py-2 text-sm font-sans rounded-md transition-all ${
                    active
                      ? 'bg-[#D4AF37]/15 text-[#D4AF37] border-l-2 border-[#D4AF37] pl-2'
                      : 'text-white/60 hover:text-white hover:bg-white/5 border-l-2 border-transparent pl-2'
                  }`}
                >
                  <Icon className="w-4 h-4 flex-shrink-0" />
                  <span className="flex-1 text-left">{item.label}</span>
                  {item.badge > 0 && (
                    <span
                      className="min-w-[18px] h-[18px] flex items-center justify-center text-[10px] font-bold rounded-full bg-red-500 text-white"
                      data-testid={`badge-${item.key}`}
                    >
                      {item.badge}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </nav>
  );

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#0A0A0A] text-white">
      <div className="lg:flex lg:min-h-screen">
        {/* Mobile overlay */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 bg-black/60 z-40 lg:hidden"
            onClick={() => setSidebarOpen(false)}
            data-testid="sidebar-overlay"
          />
        )}

        {/* Sidebar */}
        <aside
          className={`fixed lg:sticky top-0 left-0 z-50 h-screen w-[240px] bg-[#0E0E0E] border-r border-white/5 flex flex-col transition-transform lg:translate-x-0 ${
            sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
          }`}
          data-testid="admin-sidebar"
        >
          {/* Brand */}
          <div className="px-4 py-4 border-b border-white/5 flex items-center justify-between">
            <h1 className="text-base text-white font-light" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              JS Fragrance <span className="text-[#D4AF37]">Admin</span>
            </h1>
            <button
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden text-white/50 hover:text-white"
              data-testid="sidebar-close-btn"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Nav */}
          <div className="flex-1 overflow-y-auto">
            <SidebarNav />
          </div>

          {/* Footer */}
          <div className="px-3 py-3 border-t border-white/5 space-y-1">
            <DualRoleSwitcher />
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-2.5 px-2.5 py-2 text-sm font-sans rounded-md text-white/60 hover:text-red-400 hover:bg-red-500/5 transition-colors"
              data-testid="admin-logout-btn"
            >
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </aside>

        {/* Main column */}
        <div className="flex-1 lg:ml-0 min-w-0">
          {/* Top bar */}
          <header className="sticky top-0 z-30 bg-[#0A0A0A]/90 backdrop-blur-xl border-b border-white/5">
            <div className="px-4 sm:px-6 py-3 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setSidebarOpen(true)}
                  className="lg:hidden text-white/60 hover:text-white"
                  data-testid="sidebar-open-btn"
                >
                  <Menu className="w-5 h-5" />
                </button>
                <h2 className="text-white/80 text-sm font-sans uppercase tracking-[0.2em]">{activeLabel}</h2>
              </div>
              <div className="flex items-center gap-3 sm:gap-4">
                <DualRoleSwitcher variant="topbar" />
                <HealthStatusBadge />
                {totalNotif > 0 && (
                  <div className="relative" data-testid="admin-notification-bell">
                    <Bell className="w-5 h-5 text-white/50" />
                    <span className="absolute -top-1 -right-1 min-w-[16px] h-[16px] flex items-center justify-center text-[9px] font-bold rounded-full bg-red-500 text-white">
                      {totalNotif}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </header>

          {/* Content */}
          <div className="px-4 sm:px-6 py-6">


        {/* Analytics Tab */}
        {tab === 'analytics' && (
          <Suspense fallback={<TabFallback />}>
            <AnalyticsTab />
          </Suspense>
        )}


        {/* Products Tab */}
        {tab === 'products' && (
          <div data-testid="products-tab">
            <div className="bg-[#D4AF37]/5 border border-[#D4AF37]/20 p-3 mb-4 text-xs text-white/65 font-sans">
              ✨ <strong className="text-[#D4AF37]">Perfume catalog only.</strong> This tab manages your fragrance products
              (with AI scent generation, notes, gender, concentration). For Helmet Spray, Car Spray, Body Mist, and Home &amp;
              Linen, use their dedicated tabs in the sidebar.
            </div>
            <div className="flex flex-col sm:flex-row gap-3 mb-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                <input
                  type="text"
                  placeholder="Search products..."
                  value={productSearch}
                  onChange={e => setProductSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 bg-transparent border border-white/20 text-white text-sm focus:border-[#D4AF37] focus:outline-none transition-colors placeholder:text-white/30"
                  data-testid="product-search-input"
                />
              </div>
              <p className="text-white/50 text-sm font-sans self-center whitespace-nowrap">{products.filter(p => (p.category || 'Perfume').toLowerCase() === 'perfume').filter(p => {
                if (!productSearch) return true;
                const s = productSearch.toLowerCase();
                return p.name?.toLowerCase().includes(s) || p.gender?.toLowerCase().includes(s);
              }).length} products</p>
              <button
                onClick={() => { setEditingProduct(null); setShowProductModal(true); }}
                className="flex items-center gap-2 bg-[#D4AF37] text-black px-4 py-2 text-sm font-medium hover:bg-[#F3E5AB] transition-colors"
                data-testid="add-product-btn"
              >
                <Plus className="w-4 h-4" /> Add Product
              </button>
              <button
                onClick={async () => {
                  if (!window.confirm('Generate AI scent attributes for all products without them? This may take a few minutes.')) return;
                  toast.info('Generating AI attributes for all products...');
                  try {
                    const res = await axios.post(`${API}/admin/products/generate-all-attributes`, {}, { headers: getAuthHeaders() });
                    toast.success(res.data.message);
                    const pRes = await axios.get(`${API}/products`);
                    setProducts(pRes.data);
                  } catch (e) {
                    toast.error('Generation failed: ' + (e.response?.data?.detail || e.message));
                  }
                }}
                className="flex items-center gap-2 border border-[#D4AF37]/30 text-[#D4AF37] px-4 py-2 text-sm font-medium hover:bg-[#D4AF37]/10 transition-colors"
                data-testid="generate-ai-btn"
              >
                <Sparkles className="w-4 h-4" /> AI Attributes
              </button>
            </div>

            {/* Bulk Edit Panel */}
            {selectedProducts.length > 0 && (
              <div className="bg-[#D4AF37]/5 border border-[#D4AF37]/20 p-4 mb-4 space-y-3" data-testid="bulk-edit-panel">
                <div className="flex items-center justify-between">
                  <p className="text-[#D4AF37] text-sm font-sans font-medium">{selectedProducts.length} product{selectedProducts.length > 1 ? 's' : ''} selected</p>
                  <div className="flex items-center gap-2">
                    <button onClick={() => { setShowBulkEdit(!showBulkEdit); setBulkFields({}); }}
                      className="border border-[#D4AF37]/30 text-[#D4AF37] text-xs px-3 py-1.5 hover:bg-[#D4AF37]/10 transition-colors"
                      data-testid="bulk-edit-toggle">{showBulkEdit ? 'Hide Edit' : 'Bulk Edit'}</button>
                    <button onClick={async () => {
                      if (!window.confirm(`Delete ${selectedProducts.length} product(s)? This cannot be undone.`)) return;
                      try {
                        await axios.delete(`${API}/admin/products/bulk-delete`, { headers: getAuthHeaders(), data: { product_ids: selectedProducts } });
                        toast.success(`${selectedProducts.length} product(s) deleted`);
                        setSelectedProducts([]);
                        fetchData();
                      } catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
                    }} className="border border-red-500/30 text-red-400 text-xs px-3 py-1.5 hover:bg-red-500/10 transition-colors"
                      data-testid="bulk-delete-btn">Delete Selected</button>
                    <button onClick={() => { setSelectedProducts([]); setShowBulkEdit(false); setBulkFields({}); }}
                      className="text-white/30 hover:text-white/60 transition-colors" data-testid="bulk-clear-btn"><X className="w-4 h-4" /></button>
                  </div>
                </div>
                {showBulkEdit && (
                  <div className="space-y-3 pt-2 border-t border-[#D4AF37]/10">
                    <p className="text-white/30 text-xs font-sans">Only fill the fields you want to change. Empty fields stay unchanged.</p>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      <div>
                        <label className="text-white/40 text-[10px] uppercase tracking-wider font-sans block mb-1">Price</label>
                        <input type="number" placeholder="New price" value={bulkFields.base_price || ''}
                          onChange={e => setBulkFields(f => ({ ...f, base_price: e.target.value }))}
                          className="w-full bg-transparent border border-white/20 text-white text-sm px-3 py-2 focus:border-[#D4AF37] focus:outline-none"
                          data-testid="bulk-price-input" />
                      </div>
                      <div>
                        <label className="text-white/40 text-[10px] uppercase tracking-wider font-sans block mb-1">Stock</label>
                        <input type="number" placeholder="New stock" value={bulkFields.stock || ''}
                          onChange={e => setBulkFields(f => ({ ...f, stock: e.target.value }))}
                          className="w-full bg-transparent border border-white/20 text-white text-sm px-3 py-2 focus:border-[#D4AF37] focus:outline-none"
                          data-testid="bulk-stock-input" />
                      </div>
                      <div>
                        <label className="text-white/40 text-[10px] uppercase tracking-wider font-sans block mb-1">Gender</label>
                        <select value={bulkFields.gender || ''} onChange={e => setBulkFields(f => ({ ...f, gender: e.target.value }))}
                          className="w-full bg-[#141414] border border-white/20 text-white text-sm px-3 py-2"
                          data-testid="bulk-gender-select">
                          <option value="">No change</option>
                          <option value="Unisex">Unisex</option>
                          <option value="Men">Men</option>
                          <option value="Women">Women</option>
                        </select>
                      </div>
                      <div>
                        <label className="text-white/40 text-[10px] uppercase tracking-wider font-sans block mb-1">Category</label>
                        <select value={bulkFields.category || ''} onChange={e => setBulkFields(f => ({ ...f, category: e.target.value }))}
                          className="w-full bg-[#141414] border border-white/20 text-white text-sm px-3 py-2"
                          data-testid="bulk-category-select">
                          <option value="">No change</option>
                          <option value="Signature">Signature</option>
                          <option value="Inspired">Inspired</option>
                          <option value="Premium">Premium</option>
                        </select>
                      </div>
                    </div>
                    <div>
                      <label className="text-white/40 text-[10px] uppercase tracking-wider font-sans block mb-1">Description</label>
                      <textarea placeholder="New description (leave empty for no change)" value={bulkFields.description || ''}
                        onChange={e => setBulkFields(f => ({ ...f, description: e.target.value }))}
                        className="w-full bg-transparent border border-white/20 text-white text-sm px-3 py-2 focus:border-[#D4AF37] focus:outline-none resize-none h-16"
                        data-testid="bulk-description-input" />
                    </div>
                    <button onClick={async () => {
                      const updates = {};
                      Object.entries(bulkFields).forEach(([k, v]) => { if (v !== '' && v !== null && v !== undefined) updates[k] = v; });
                      if (!Object.keys(updates).length) { toast.error('Fill at least one field'); return; }
                      try {
                        const res = await axios.put(`${API}/admin/products/bulk-update`, { product_ids: selectedProducts, updates }, { headers: getAuthHeaders() });
                        toast.success(`Updated ${res.data.modified} product(s): ${res.data.fields.join(', ')}`);
                        setSelectedProducts([]); setShowBulkEdit(false); setBulkFields({});
                        fetchData();
                      } catch (e) { toast.error(e.response?.data?.detail || 'Bulk update failed'); }
                    }} className="bg-[#D4AF37] text-black px-6 py-2 text-sm font-medium hover:bg-[#F3E5AB] transition-colors"
                      data-testid="bulk-save-btn">
                      Apply to {selectedProducts.length} Product{selectedProducts.length > 1 ? 's' : ''}
                    </button>
                  </div>
                )}
              </div>
            )}

            <div className="overflow-x-auto">
              <table className="w-full" data-testid="products-table">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="py-3 px-2 w-10">
                      <button onClick={() => {
                        const filtered = products.filter(p => (p.category || 'Perfume').toLowerCase() === 'perfume').filter(p => {
                          if (!productSearch) return true;
                          const s = productSearch.toLowerCase();
                          return p.name?.toLowerCase().includes(s) || p.gender?.toLowerCase().includes(s) || (p.is_signature && 'signature'.includes(s));
                        }).map(p => p.id);
                        setSelectedProducts(prev => prev.length === filtered.length ? [] : filtered);
                      }} className="text-white/30 hover:text-[#D4AF37] transition-colors" data-testid="select-all-btn">
                        {(() => {
                          const filtered = products.filter(p => (p.category || 'Perfume').toLowerCase() === 'perfume').filter(p => { if (!productSearch) return true; const s = productSearch.toLowerCase(); return p.name?.toLowerCase().includes(s) || p.gender?.toLowerCase().includes(s); });
                          return selectedProducts.length > 0 && selectedProducts.length === filtered.length
                            ? <CheckSquare className="w-4 h-4 text-[#D4AF37]" />
                            : <Square className="w-4 h-4" />;
                        })()}
                      </button>
                    </th>
                    <th className="text-left text-xs uppercase tracking-wider text-white/40 font-sans font-medium py-3 px-2">Product</th>
                    <th className="text-left text-xs uppercase tracking-wider text-white/40 font-sans font-medium py-3 px-2">Gender</th>
                    <th className="text-left text-xs uppercase tracking-wider text-white/40 font-sans font-medium py-3 px-2">Price</th>
                    <th className="text-left text-xs uppercase tracking-wider text-white/40 font-sans font-medium py-3 px-2">Stock</th>
                    <th className="text-left text-xs uppercase tracking-wider text-white/40 font-sans font-medium py-3 px-2">Variants</th>
                    <th className="text-right text-xs uppercase tracking-wider text-white/40 font-sans font-medium py-3 px-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    const filteredProds = products.filter(p => (p.category || 'Perfume').toLowerCase() === 'perfume').filter(p => {
                      if (!productSearch) return true;
                      const s = productSearch.toLowerCase();
                      return p.name?.toLowerCase().includes(s) || p.gender?.toLowerCase().includes(s) || (p.is_signature && 'signature'.includes(s));
                    });
                    const paged = filteredProds.slice((productsPage - 1) * PAGE_SIZE, productsPage * PAGE_SIZE);
                    return paged.map(p => (
                    <tr key={p.id} className={`border-b border-white/5 transition-colors ${selectedProducts.includes(p.id) ? 'bg-[#D4AF37]/5' : 'hover:bg-white/5'}`}>
                      <td className="py-3 px-2">
                        <button onClick={() => setSelectedProducts(prev => prev.includes(p.id) ? prev.filter(x => x !== p.id) : [...prev, p.id])}
                          className="text-white/30 hover:text-[#D4AF37] transition-colors" data-testid={`select-product-${p.id}`}>
                          {selectedProducts.includes(p.id)
                            ? <CheckSquare className="w-4 h-4 text-[#D4AF37]" />
                            : <Square className="w-4 h-4" />}
                        </button>
                      </td>
                      <td className="py-3 px-2">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-[#141414] overflow-hidden flex-shrink-0">
                            <img src={p.image_url || ''} alt="" className="w-full h-full object-cover" />
                          </div>
                          <div>
                            <p className="text-white text-sm">{p.name}</p>
                            <p className="text-white/30 text-xs truncate max-w-[200px]">{p.description}</p>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-2 text-white/60 text-sm">{p.gender}</td>
                      <td className="py-3 px-2 text-[#D4AF37] text-sm font-sans">₱{p.base_price?.toLocaleString()}</td>
                      <td className="py-3 px-2 text-white/60 text-sm">{p.stock}</td>
                      <td className="py-3 px-2 text-white/60 text-sm">{p.enable_variants ? `${p.variants?.length || 0} variants` : 'Off'}</td>
                      <td className="py-3 px-2 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button onClick={async () => {
                            try {
                              await axios.put(`${API}/admin/products/${p.id}/featured`, {}, { headers: getAuthHeaders() });
                              fetchData();
                            } catch { toast.error('Failed to toggle'); }
                          }} className={`p-1.5 transition-colors ${p.is_featured ? 'text-[#D4AF37]' : 'text-white/20 hover:text-[#D4AF37]/60'}`} data-testid={`featured-toggle-${p.id}`} title={p.is_featured ? 'Remove from featured' : 'Set as featured'}>
                            <Star className="w-4 h-4" fill={p.is_featured ? 'currentColor' : 'none'} />
                          </button>
                          <button onClick={() => { setEditingProduct(p); setShowProductModal(true); }} className="p-1.5 text-white/40 hover:text-[#D4AF37] transition-colors" data-testid={`edit-product-${p.id}`}>
                            <Pencil className="w-4 h-4" />
                          </button>
                          <button onClick={() => handleDeleteProduct(p.id)} className="p-1.5 text-white/40 hover:text-red-400 transition-colors" data-testid={`delete-product-${p.id}`}>
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ));
                  })()}
                </tbody>
              </table>
            </div>
            {(() => {
              const filteredProds = products.filter(p => (p.category || 'Perfume').toLowerCase() === 'perfume').filter(p => {
                if (!productSearch) return true;
                const s = productSearch.toLowerCase();
                return p.name?.toLowerCase().includes(s) || p.gender?.toLowerCase().includes(s) || (p.is_signature && 'signature'.includes(s));
              });
              const total = filteredProds.length;
              const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
              return (
                <Pagination
                  page={productsPage}
                  pages={pages}
                  total={total}
                  limit={PAGE_SIZE}
                  onChange={setProductsPage}
                  testIdPrefix="products-pagination"
                />
              );
            })()}
          </div>
        )}

        {/* Orders Tab */}
        {tab === 'orders' && (
          <div data-testid="orders-tab">
            <div className="flex flex-col sm:flex-row gap-3 mb-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                <input
                  type="text"
                  placeholder="Search orders..."
                  value={orderSearch}
                  onChange={e => setOrderSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 bg-transparent border border-white/20 text-white text-sm focus:border-[#D4AF37] focus:outline-none transition-colors placeholder:text-white/30"
                  data-testid="order-search-input"
                />
              </div>
              <select
                value={orderFilter}
                onChange={e => setOrderFilter(e.target.value)}
                className="bg-[#141414] border border-white/20 text-white text-sm px-3 py-2 focus:border-[#D4AF37] focus:outline-none"
                data-testid="order-status-filter"
              >
                <option value="">All Statuses</option>
                {['Pending Payment', 'Paid', 'Preparing', 'Shipped', 'Completed', 'Rejected'].map(s => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
              <button
                onClick={handleExportCSV}
                className="flex items-center gap-2 border border-white/20 text-white/60 hover:text-white px-4 py-2 text-sm transition-colors"
                data-testid="export-csv-btn"
              >
                <Download className="w-4 h-4" /> Export CSV
              </button>
              <button
                onClick={async () => {
                  // Two-step confirm: preview first, then destructive delete.
                  try {
                    const preview = await axios.post(
                      `${API}/admin/orders/purge-test`,
                      { dry_run: true },
                      { headers: getAuthHeaders() },
                    );
                    const n = preview.data.would_delete;
                    if (!n) { toast.info('No test orders found to purge.'); return; }
                    if (!window.confirm(`Permanently delete ${n} test order${n === 1 ? '' : 's'}?\n\nThis removes orders where:\n• Customer name starts with "TEST"\n• Email is @example.com / @test.com / @qa.local\n• Phone is 09999999999 / 0000000000 / 9999999999`)) return;
                    const r = await axios.post(
                      `${API}/admin/orders/purge-test`,
                      {},
                      { headers: getAuthHeaders() },
                    );
                    toast.success(`Deleted ${r.data.deleted} test order${r.data.deleted === 1 ? '' : 's'}`);
                    fetchData();
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || 'Failed to purge test orders');
                  }
                }}
                className="flex items-center gap-2 border border-red-400/30 text-red-400/80 hover:text-red-400 hover:border-red-400/60 px-4 py-2 text-sm transition-colors"
                data-testid="purge-test-orders-btn"
                title="Bulk-delete demo / QA orders (TEST* names or *@example.com emails)"
              >
                <Trash2 className="w-4 h-4" /> Purge Test Orders
              </button>
            </div>

            <p className="text-white/40 text-sm mb-4 font-sans">{ordersTotal} orders {orderSearch || orderFilter ? '(filtered)' : ''}</p>

            <div className="space-y-2" data-testid="orders-list">
              {pagedOrders.map(order => (
                <div key={order.id} className="bg-[#141414] border border-white/5">
                  <div
                    className="flex items-center justify-between p-4 cursor-pointer hover:bg-white/5 transition-colors"
                    onClick={() => setExpandedOrder(expandedOrder === order.id ? null : order.id)}
                    data-testid={`order-row-${order.id}`}
                  >
                    <div className="flex items-center gap-4 min-w-0">
                      <div>
                        <p className="text-white text-sm font-mono">{order.order_number}</p>
                        <p className="text-white/40 text-xs">{order.customer_name}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`text-xs px-2 py-1 font-sans ${getStatusColor(order.status)}`}>
                        {formatStatusLabel(order.status)}
                      </span>
                      <span className="text-[#D4AF37] text-sm font-sans">₱{order.total?.toLocaleString()}</span>
                      {expandedOrder === order.id ? <ChevronUp className="w-4 h-4 text-white/40" /> : <ChevronDown className="w-4 h-4 text-white/40" />}
                    </div>
                  </div>

                  {expandedOrder === order.id && (
                    <div className="border-t border-white/5 p-4 space-y-4" data-testid={`order-details-${order.id}`}>
                      {/* Customer */}
                      <div>
                        <p className="text-xs uppercase tracking-wider text-white/40 mb-1 font-sans">Customer</p>
                        <p className="text-white text-sm">{order.customer_name}</p>
                        <p className="text-white/50 text-sm">{order.phone}</p>
                        <p className="text-white/50 text-sm">{order.address?.house}, {order.address?.barangay}, {order.address?.city}, {order.address?.province} {order.address?.zip}</p>
                      </div>

                      {/* Items */}
                      <div>
                        <p className="text-xs uppercase tracking-wider text-white/40 mb-1 font-sans">Items</p>
                        {order.items?.map((item, i) => (
                          <div key={i} className="flex justify-between text-sm py-1">
                            <span className="text-white/70">{item.product_name} x{item.quantity} {item.variant ? `(${item.variant.size}, ${item.variant.type})` : ''}</span>
                            <span className="text-white/50">₱{(item.price * item.quantity).toLocaleString()}</span>
                          </div>
                        ))}
                      </div>

                      {/* Payment */}
                      <div>
                        <p className="text-xs uppercase tracking-wider text-white/40 mb-1 font-sans">Payment</p>
                        <p className="text-white text-sm capitalize">{order.payment_method?.replace('_', ' ')}</p>
                        {order.proof_storage_path && (
                          <a
                            href={`${API}/files/${order.proof_storage_path}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-[#D4AF37] text-sm mt-1 hover:underline"
                            data-testid={`view-proof-${order.id}`}
                          >
                            <Eye className="w-3 h-3" /> View Proof
                          </a>
                        )}
                      </div>

                      {/* Tracking */}
                      <div>
                        <p className="text-xs uppercase tracking-wider text-white/40 mb-1 font-sans">Tracking Number</p>
                        <input
                          type="text"
                          defaultValue={order.tracking_number || ''}
                          placeholder="Enter tracking number"
                          onBlur={e => { if (e.target.value !== (order.tracking_number || '')) handleTrackingUpdate(order.id, e.target.value); }}
                          className="w-full bg-transparent border-b border-white/20 text-white py-2 text-sm focus:border-[#D4AF37] focus:outline-none placeholder:text-white/20"
                          data-testid={`tracking-input-${order.id}`}
                        />
                      </div>

                      {/* Status Actions */}
                      <div>
                        <p className="text-xs uppercase tracking-wider text-white/40 mb-2 font-sans">Update Status</p>
                        <div className="flex flex-wrap gap-2">
                          {['Pending Payment', 'Paid', 'Preparing', 'Shipped', 'Completed', 'Rejected'].map(s => (
                            <button
                              key={s}
                              onClick={() => handleStatusUpdate(order.id, s)}
                              disabled={order.status === s}
                              data-testid={`status-btn-${s.toLowerCase().replace(' ', '-')}-${order.id}`}
                              className={`text-xs px-3 py-1.5 transition-colors ${
                                order.status === s
                                  ? 'bg-[#D4AF37] text-black font-medium'
                                  : 'border border-white/20 text-white/50 hover:border-white/40 hover:text-white'
                              }`}
                            >
                              {s}
                            </button>
                          ))}
                        </div>
                      </div>

                      <p className="text-white/20 text-xs font-sans">Created: {new Date(order.created_at).toLocaleString()}</p>
                    </div>
                  )}
                </div>
              ))}
              {filteredOrders.length === 0 && (
                <div className="text-center py-12">
                  <p className="text-white/40 text-sm">No orders found</p>
                </div>
              )}
            </div>
            <Pagination
              page={ordersPage}
              pages={ordersPages}
              total={ordersTotal}
              limit={PAGE_SIZE}
              onChange={setOrdersPage}
              testIdPrefix="orders-pagination"
            />
          </div>
        )}


        {tab === 'reviews' && (
          <Suspense fallback={<TabFallback />}>
            <ReviewsModerationTab />
          </Suspense>
        )}

        {tab === 'ambassadors' && (
          <Suspense fallback={<TabFallback />}>
            <AmbassadorsTab />
          </Suspense>
        )}

        {tab === 'audit' && <AuditLogTab />}

        {tab === 'faq' && (
          <Suspense fallback={<TabFallback />}>
            <FAQTab />
          </Suspense>
        )}

        {tab === 'blog' && (
          <Suspense fallback={<TabFallback />}>
            <BlogTab />
          </Suspense>
        )}

        {tab === 'inbox' && (
          <Suspense fallback={<TabFallback />}>
            <ContactInboxTab />
          </Suspense>
        )}

        {tab === 'webhooks' && (
          <Suspense fallback={<TabFallback />}>
            <WebhooksTab />
          </Suspense>
        )}

        {tab === 'ambassador_apps' && (
          <Suspense fallback={<TabFallback />}>
            <AmbassadorApplicationsTab />
          </Suspense>
        )}

        {tab === 'tester_kit_orders' && (
          <Suspense fallback={<TabFallback />}>
            <TesterKitOrdersTab />
          </Suspense>
        )}

        {tab === 'bulk_orders' && (
          <Suspense fallback={<TabFallback />}>
            <BulkOrdersTab />
          </Suspense>
        )}

        {tab === 'announcements' && <AnnouncementsTab />}

        {tab === 'withdrawals' && <WithdrawalsTab />}
        {tab === 'prize_claims' && <PrizeClaimsTab />}

        {tab === 'coaching_tips' && <CoachingTipsTab />}

        {tab === 'business_config' && (
          <Suspense fallback={<TabFallback />}>
            <BusinessConfigTab />
          </Suspense>
        )}

        {/* Per-category catalog tabs — rendered for any tab key starting with "cat:" */}
        {tab.startsWith('cat:') && (() => {
          const slug = tab.slice(4);
          const tpl = categoryTemplates.find(t => t.slug === slug);
          if (!tpl) return <p className="p-6 text-white/40 text-sm font-sans">Loading category…</p>;
          return (
            <Suspense fallback={<TabFallback />}>
              <SimpleProductsTab template={tpl} />
            </Suspense>
          );
        })()}


        {/* Settings Tab */}
        {tab === 'settings' && (
          <div data-testid="settings-tab" className="max-w-lg space-y-6">
            <h2 className="text-xl text-white mb-6" style={{ fontFamily: 'Cormorant Garamond, serif' }}>Store Settings</h2>

            {/* COD Toggle */}
            <div className="bg-[#141414] border border-white/5 p-4 sm:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white text-sm font-sans font-medium">Cash on Delivery</p>
                  <p className="text-white/40 text-xs font-sans mt-1">Allow customers to pay upon delivery</p>
                </div>
                <Switch
                  checked={settings.cod_enabled}
                  onCheckedChange={handleCODToggle}
                  data-testid="cod-toggle"
                />
              </div>
            </div>

            {/* Shipping Fees */}
            <ShippingFeeSettings settings={settings} onUpdate={(updated) => setSettings(updated)} />

            {/* Bundle Config */}
            <div className="bg-[#141414] border border-white/5 p-4 sm:p-6">
              <p className="text-white text-sm font-sans font-medium mb-4">Bundle Builder</p>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-white/60 text-xs font-sans">Enabled</span>
                  <Switch
                    checked={settings.bundle_config?.enabled ?? true}
                    onCheckedChange={async (val) => {
                      const updated = { ...settings, bundle_config: { ...(settings.bundle_config || {}), enabled: val } };
                      setSettings(updated);
                      await axios.put(`${API}/admin/settings`, updated, { headers: getAuthHeaders() });
                      toast.success('Bundle ' + (val ? 'enabled' : 'disabled'));
                    }}
                    data-testid="bundle-toggle"
                  />
                </div>
                <div>
                  <label className="text-white/60 text-xs font-sans block mb-1">Title (heading shown at top of /bundle page)</label>
                  <input
                    type="text"
                    value={settings.bundle_config?.title ?? ''}
                    placeholder="Build Your Bundle"
                    onChange={e => setSettings(s => ({ ...s, bundle_config: { ...(s.bundle_config || {}), title: e.target.value } }))}
                    className="w-full bg-transparent border border-white/20 text-white text-sm px-3 py-2 focus:border-[#D4AF37] focus:outline-none"
                    data-testid="bundle-title-input"
                  />
                </div>
                <div>
                  <label className="text-white/60 text-xs font-sans block mb-1">Subtitle (the small line under the heading)</label>
                  <input
                    type="text"
                    value={settings.bundle_config?.subtitle ?? ''}
                    placeholder="Pick 3 scents and save 15%"
                    onChange={e => setSettings(s => ({ ...s, bundle_config: { ...(s.bundle_config || {}), subtitle: e.target.value } }))}
                    className="w-full bg-transparent border border-white/20 text-white text-sm px-3 py-2 focus:border-[#D4AF37] focus:outline-none"
                    data-testid="bundle-subtitle-input"
                  />
                </div>
                <div>
                  <label className="text-white/60 text-xs font-sans block mb-1">Bundle Size</label>
                  <input
                    type="number"
                    value={settings.bundle_config?.bundle_size || 3}
                    onChange={e => setSettings(s => ({ ...s, bundle_config: { ...(s.bundle_config || {}), bundle_size: parseInt(e.target.value) || 3 } }))}
                    className="w-full bg-transparent border border-white/20 text-white text-sm px-3 py-2 focus:border-[#D4AF37] focus:outline-none"
                    data-testid="bundle-size-input"
                  />
                </div>
                <div>
                  <label className="text-white/60 text-xs font-sans block mb-1">Discount %</label>
                  <input
                    type="number"
                    value={settings.bundle_config?.discount_percent || 15}
                    onChange={e => setSettings(s => ({ ...s, bundle_config: { ...(s.bundle_config || {}), discount_percent: parseInt(e.target.value) || 15 } }))}
                    className="w-full bg-transparent border border-white/20 text-white text-sm px-3 py-2 focus:border-[#D4AF37] focus:outline-none"
                    data-testid="bundle-discount-input"
                  />
                </div>
                <button
                  onClick={async () => {
                    await axios.put(`${API}/admin/settings`, settings, { headers: getAuthHeaders() });
                    toast.success('Bundle settings saved');
                  }}
                  className="bg-[#D4AF37] text-black px-4 py-2 text-sm font-medium hover:bg-[#F3E5AB] transition-colors"
                  data-testid="save-bundle-settings"
                >
                  Save Bundle Settings
                </button>
              </div>
            </div>

            {/* Marketing & Tracking */}
            <div className="bg-[#141414] border border-white/5 p-4 sm:p-6">
              <div className="flex items-center gap-2 mb-1">
                <BarChart3 className="w-4 h-4 text-[#D4AF37]" />
                <p className="text-white text-sm font-sans font-medium">Marketing &amp; Tracking</p>
              </div>
              <p className="text-white/40 text-xs font-sans mb-4">
                Connect Meta Pixel for Facebook / Instagram ads conversion tracking. GA4 is already wired (managed in code).
              </p>
              <div className="space-y-3">
                <div>
                  <label className="text-white/60 text-xs font-sans block mb-1">
                    Meta Pixel ID
                    <a href="https://www.facebook.com/business/help/952192354843755" target="_blank" rel="noopener noreferrer" className="text-[#D4AF37]/60 hover:text-[#D4AF37] ml-2 text-[10px]">where do I find this?</a>
                  </label>
                  <input
                    type="text"
                    value={settings.meta_pixel_id || ''}
                    placeholder="e.g. 1234567890123456"
                    onChange={e => setSettings(s => ({ ...s, meta_pixel_id: e.target.value }))}
                    className="w-full bg-transparent border border-white/20 text-white text-sm px-3 py-2 focus:border-[#D4AF37] focus:outline-none font-mono"
                    data-testid="meta-pixel-id-input"
                  />
                </div>
                <div>
                  <p className="text-white/60 text-xs font-sans mb-1">Google Analytics 4 Measurement ID <span className="text-white/30">(read-only — embedded in code)</span></p>
                  <p className="text-white/50 text-xs font-mono bg-white/5 px-3 py-2 border border-white/10">G-XPN6L041F5</p>
                </div>
                <button
                  onClick={async () => {
                    await axios.put(`${API}/admin/settings`, settings, { headers: getAuthHeaders() });
                    toast.success('Tracking settings saved — page will reload to activate Meta Pixel.');
                    setTimeout(() => window.location.reload(), 1500);
                  }}
                  className="bg-[#D4AF37] text-black px-4 py-2 text-sm font-medium hover:bg-[#F3E5AB] transition-colors"
                  data-testid="save-tracking-settings"
                >
                  Save Tracking Settings
                </button>
              </div>
            </div>

            {/* Meta Conversions API health widget */}
            <CapiHealthCard getAuthHeaders={getAuthHeaders} />


            {/* Hero Banner Image */}
            <div className="bg-[#141414] border border-white/5 p-4 sm:p-6">
              <p className="text-white text-sm font-sans font-medium mb-4">Homepage Hero Banner</p>
              <div
                className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors cursor-pointer ${
                  ogDragging ? 'border-[#1C7D3F] bg-[#1C7D3F]/5' : 'border-white/20 hover:border-white/40'
                }`}
                data-testid="hero-image-dropzone"
                onDragOver={e => { e.preventDefault(); }}
                onDrop={async e => {
                  e.preventDefault();
                  const file = e.dataTransfer.files[0];
                  if (!file || !file.type.startsWith('image/')) { toast.error('Images only'); return; }
                  setOgUploading(true);
                  try {
                    const fd = new FormData(); fd.append('file', file);
                    const res = await axios.post(`${API}/upload/product-image`, fd);
                    const url = `${window.location.origin}/api/files/${res.data.storage_path}`;
                    const updated = { ...settings, hero_image: url };
                    await axios.put(`${API}/admin/settings`, updated, { headers: getAuthHeaders() });
                    setSettings(updated);
                    try { localStorage.setItem('jsf_settings_cache', JSON.stringify(updated)); } catch {}
                    toast.success('Banner updated!');
                  } catch { toast.error('Upload failed'); }
                  setOgUploading(false);
                }}
                onClick={() => document.getElementById('hero-banner-input').click()}
              >
                <input id="hero-banner-input" type="file" accept="image/*" className="hidden" onChange={async e => {
                  const file = e.target.files[0]; if (!file) return;
                  setOgUploading(true);
                  try {
                    const fd = new FormData(); fd.append('file', file);
                    const res = await axios.post(`${API}/upload/product-image`, fd);
                    const url = `${window.location.origin}/api/files/${res.data.storage_path}`;
                    const updated = { ...settings, hero_image: url };
                    await axios.put(`${API}/admin/settings`, updated, { headers: getAuthHeaders() });
                    setSettings(updated);
                    try { localStorage.setItem('jsf_settings_cache', JSON.stringify(updated)); } catch {}
                    toast.success('Banner updated!');
                  } catch { toast.error('Upload failed'); }
                  setOgUploading(false);
                  e.target.value = '';
                }} />
                {ogUploading ? (
                  <div className="flex flex-col items-center gap-2">
                    <Loader2 className="w-8 h-8 text-[#1C7D3F] animate-spin" />
                    <p className="text-white/50 text-sm font-sans">Uploading...</p>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-2">
                    <Upload className="w-8 h-8 text-white/30" />
                    <p className="text-white/50 text-sm font-sans">Drag & drop banner image, or click to browse</p>
                    <p className="text-white/30 text-xs font-sans">Recommended: 1920x800px (JPG/PNG/WebP)</p>
                  </div>
                )}
              </div>
              {settings.hero_image && (
                <div className="mt-3">
                  <p className="text-white/40 text-[10px] uppercase tracking-[0.2em] font-sans mb-2">Current Banner</p>
                  <div className="w-full max-w-md h-24 bg-[#141414] overflow-hidden border border-white/10 rounded-sm">
                    <img src={settings.hero_image} alt="Current hero banner" className="w-full h-full object-cover" />
                  </div>
                  <p className="text-white/30 text-[10px] font-sans mt-2">Upload a new banner above to replace this. The previous file will be automatically removed from storage.</p>
                </div>
              )}
            </div>


            {/* Custom Scent Config */}
            <div className="bg-[#141414] border border-white/5 p-4 sm:p-6">
              <p className="text-white text-sm font-sans font-medium mb-4">Custom Scent Builder</p>
              <div className="space-y-4">
                {/* Enable/Disable Toggle */}
                <div className="flex items-center justify-between">
                  <span className="text-white/60 text-xs font-sans">Enabled</span>
                  <Switch
                    checked={settings.custom_scent_config?.enabled ?? true}
                    onCheckedChange={async (val) => {
                      const updated = { ...settings, custom_scent_config: { ...(settings.custom_scent_config || {}), enabled: val } };
                      setSettings(updated);
                      await axios.put(`${API}/admin/settings`, updated, { headers: getAuthHeaders() });
                      toast.success('Custom Scent ' + (val ? 'enabled' : 'disabled'));
                    }}
                    data-testid="custom-scent-toggle"
                  />
                </div>

                {/* Max Name Length */}
                <div>
                  <label className="text-white/60 text-xs font-sans block mb-1">Max Name Length</label>
                  <input
                    type="number"
                    value={settings.custom_scent_config?.max_name_length || 25}
                    onChange={e => setSettings(s => ({ ...s, custom_scent_config: { ...(s.custom_scent_config || {}), max_name_length: parseInt(e.target.value) || 25 } }))}
                    className="w-full sm:w-48 bg-transparent border border-white/20 text-white text-sm px-3 py-2 focus:border-[#D4AF37] focus:outline-none"
                    data-testid="cs-max-name-input"
                  />
                </div>

                {/* Sizes & Pricing Interactive Table */}
                <div>
                  <label className="text-white/60 text-xs font-sans block mb-2">Sizes & Pricing</label>
                  <div className="border border-white/10 rounded overflow-hidden">
                    {/* Table Header */}
                    <div className="hidden sm:grid sm:grid-cols-[40px_1fr_1fr_1fr_40px] bg-white/5 text-white/50 text-xs uppercase tracking-wider font-sans">
                      <div className="px-2 py-2"></div>
                      <div className="px-3 py-2">Size</div>
                      <div className="px-3 py-2">Type</div>
                      <div className="px-3 py-2">Price (PHP)</div>
                      <div className="px-2 py-2"></div>
                    </div>
                    {/* Table Rows */}
                    {(settings.custom_scent_config?.sizes || []).map((row, idx) => (
                      <div
                        key={idx}
                        className="grid grid-cols-1 sm:grid-cols-[40px_1fr_1fr_1fr_40px] gap-2 sm:gap-0 border-t border-white/5 p-2 sm:p-0 items-center group hover:bg-white/[0.02] transition-colors"
                        data-testid={`cs-size-row-${idx}`}
                        draggable
                        onDragStart={e => { e.dataTransfer.setData('text/plain', idx.toString()); e.currentTarget.style.opacity = '0.5'; }}
                        onDragEnd={e => { e.currentTarget.style.opacity = '1'; }}
                        onDragOver={e => e.preventDefault()}
                        onDrop={e => {
                          e.preventDefault();
                          const fromIdx = parseInt(e.dataTransfer.getData('text/plain'));
                          if (fromIdx === idx) return;
                          setSettings(s => {
                            const sizes = [...(s.custom_scent_config?.sizes || [])];
                            const [moved] = sizes.splice(fromIdx, 1);
                            sizes.splice(idx, 0, moved);
                            return { ...s, custom_scent_config: { ...(s.custom_scent_config || {}), sizes } };
                          });
                        }}
                      >
                        {/* Drag Handle */}
                        <div className="hidden sm:flex items-center justify-center cursor-grab active:cursor-grabbing text-white/20 hover:text-white/50">
                          <GripVertical className="w-4 h-4" />
                        </div>
                        {/* Size Input */}
                        <div className="sm:px-1 sm:py-1.5">
                          <label className="text-white/40 text-[10px] font-sans sm:hidden mb-0.5 block">SIZE</label>
                          <input
                            type="text"
                            value={row.size || ''}
                            placeholder="e.g. 30ml"
                            onChange={e => {
                              setSettings(s => {
                                const sizes = [...(s.custom_scent_config?.sizes || [])];
                                sizes[idx] = { ...sizes[idx], size: e.target.value };
                                return { ...s, custom_scent_config: { ...(s.custom_scent_config || {}), sizes } };
                              });
                            }}
                            className="w-full bg-transparent border border-white/10 text-white text-sm px-2 py-1.5 focus:border-[#D4AF37] focus:outline-none rounded-sm"
                            data-testid={`cs-size-input-${idx}`}
                          />
                        </div>
                        {/* Type Dropdown */}
                        <div className="sm:px-1 sm:py-1.5">
                          <label className="text-white/40 text-[10px] font-sans sm:hidden mb-0.5 block">TYPE</label>
                          <select
                            value={row.type || 'EDP'}
                            onChange={e => {
                              setSettings(s => {
                                const sizes = [...(s.custom_scent_config?.sizes || [])];
                                sizes[idx] = { ...sizes[idx], type: e.target.value };
                                return { ...s, custom_scent_config: { ...(s.custom_scent_config || {}), sizes } };
                              });
                            }}
                            className="w-full bg-[#1a1a1a] border border-white/10 text-white text-sm px-2 py-1.5 focus:border-[#D4AF37] focus:outline-none rounded-sm appearance-none"
                            data-testid={`cs-type-select-${idx}`}
                          >
                            <option value="EDP">EDP</option>
                            <option value="EDT">EDT</option>
                            <option value="Parfum">Parfum</option>
                            <option value="Body Mist">Body Mist</option>
                          </select>
                        </div>
                        {/* Price Input */}
                        <div className="sm:px-1 sm:py-1.5">
                          <label className="text-white/40 text-[10px] font-sans sm:hidden mb-0.5 block">PRICE (PHP)</label>
                          <input
                            type="number"
                            value={row.price ?? ''}
                            placeholder="0"
                            min="0"
                            onChange={e => {
                              setSettings(s => {
                                const sizes = [...(s.custom_scent_config?.sizes || [])];
                                sizes[idx] = { ...sizes[idx], price: e.target.value === '' ? '' : Number(e.target.value) };
                                return { ...s, custom_scent_config: { ...(s.custom_scent_config || {}), sizes } };
                              });
                            }}
                            className="w-full bg-transparent border border-white/10 text-white text-sm px-2 py-1.5 focus:border-[#D4AF37] focus:outline-none rounded-sm"
                            data-testid={`cs-price-input-${idx}`}
                          />
                        </div>
                        {/* Delete Button */}
                        <div className="flex items-center justify-center">
                          <button
                            onClick={() => {
                              setSettings(s => {
                                const sizes = [...(s.custom_scent_config?.sizes || [])];
                                sizes.splice(idx, 1);
                                return { ...s, custom_scent_config: { ...(s.custom_scent_config || {}), sizes } };
                              });
                            }}
                            className="text-red-400/40 hover:text-red-400 transition-colors p-1"
                            data-testid={`cs-delete-row-${idx}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                    {/* Empty State */}
                    {(!settings.custom_scent_config?.sizes || settings.custom_scent_config.sizes.length === 0) && (
                      <div className="text-center py-6 text-white/30 text-xs font-sans">
                        No sizes added yet. Click the button below to add one.
                      </div>
                    )}
                  </div>
                  {/* Add Row Button */}
                  <button
                    onClick={() => {
                      setSettings(s => {
                        const sizes = [...(s.custom_scent_config?.sizes || [])];
                        sizes.push({ size: '30ml', type: 'EDP', price: 0 });
                        return { ...s, custom_scent_config: { ...(s.custom_scent_config || {}), sizes } };
                      });
                    }}
                    className="mt-2 flex items-center gap-1.5 text-[#D4AF37]/70 hover:text-[#D4AF37] text-xs font-sans transition-colors"
                    data-testid="cs-add-size-btn"
                  >
                    <Plus className="w-3.5 h-3.5" /> Add Size Option
                  </button>
                </div>

                {/* Save Button */}
                <div className="flex items-center gap-3 pt-2">
                  <button
                    onClick={async () => {
                      const sizes = settings.custom_scent_config?.sizes || [];
                      const invalid = sizes.some(r => !r.size?.trim() || !r.type?.trim() || r.price === '' || r.price === undefined || isNaN(Number(r.price)));
                      if (invalid) {
                        toast.error('All rows need a valid size, type, and numeric price');
                        return;
                      }
                      const cleanSizes = sizes.map(r => ({ size: r.size.trim(), type: r.type.trim(), price: Number(r.price) }));
                      const updated = { ...settings, custom_scent_config: { ...(settings.custom_scent_config || {}), sizes: cleanSizes } };
                      await axios.put(`${API}/admin/settings`, updated, { headers: getAuthHeaders() });
                      setSettings(updated);
                      toast.success('Custom Scent settings saved');
                    }}
                    className="bg-[#D4AF37] text-black px-5 py-2 text-sm font-medium hover:bg-[#F3E5AB] transition-colors"
                    data-testid="save-cs-settings"
                  >
                    Save Custom Scent Settings
                  </button>
                </div>
              </div>
            </div>

            {/* 2FA Security */}
            <Suspense fallback={<TabFallback />}>
              <TwoFactorSetup />
            </Suspense>

            {/* Push Broadcast */}
            <Suspense fallback={<TabFallback />}>
              <PushBroadcastPanel />
            </Suspense>

            {/* Weekly Email Digest */}
            <Suspense fallback={<TabFallback />}>
              <DigestConfigPanel />
            </Suspense>

            {/* Storage Usage */}
            <StorageUsageWidget />

            {/* Audit Logs (collapsed widget — full page available in Configuration → Audit Log) */}
            <AuditLogViewer />
          </div>
        )}

        {/* Collections Tab */}
        {tab === 'collections' && (
          <div data-testid="collections-tab">
            <div className="flex justify-between items-center mb-4">
              <p className="text-white/50 text-sm font-sans">{collections.length} collections</p>
              <button
                onClick={() => { setEditingCollection(null); setShowCollectionModal(true); }}
                className="flex items-center gap-2 bg-[#D4AF37] text-black px-4 py-2 text-sm font-medium hover:bg-[#F3E5AB] transition-colors"
                data-testid="add-collection-btn"
              >
                <Plus className="w-4 h-4" /> Add Collection
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {collections.map(col => (
                <div key={col.id} className="bg-[#141414] border border-white/10 overflow-hidden group" data-testid={`collection-card-${col.id}`}>
                  <div className="relative aspect-[4/3] overflow-hidden">
                    {col.image_url ? (
                      <img src={col.image_url} alt={col.title} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-white/5">
                        <Image className="w-8 h-8 text-white/20" />
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                    <div className="absolute bottom-3 left-3">
                      <p className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37] font-sans">{col.subtitle}</p>
                      <p className="text-white text-lg font-light" style={{ fontFamily: 'Cormorant Garamond, serif' }}>{col.title}</p>
                    </div>
                    {!col.is_active && (
                      <div className="absolute top-2 right-2 bg-red-500/80 text-white text-[10px] uppercase px-2 py-0.5 font-sans">Hidden</div>
                    )}
                  </div>
                  <div className="p-3 flex items-center justify-between">
                    <div>
                      <p className="text-white/40 text-xs font-sans truncate max-w-[180px]">Links to: {col.link_url}</p>
                      <p className="text-white/30 text-xs font-sans">Order: {col.sort_order}</p>
                    </div>
                    <div className="flex gap-1.5">
                      <button onClick={() => { setEditingCollection(col); setShowCollectionModal(true); }}
                        className="p-1.5 text-white/40 hover:text-[#D4AF37] transition-colors" data-testid={`edit-collection-${col.id}`}>
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={async () => {
                        if (!window.confirm('Delete this collection?')) return;
                        try {
                          await axios.delete(`${API}/admin/collections/${col.id}`, { headers: getAuthHeaders() });
                          toast.success('Collection deleted');
                          fetchData();
                        } catch { toast.error('Failed to delete'); }
                      }} className="p-1.5 text-white/40 hover:text-red-400 transition-colors" data-testid={`delete-collection-${col.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Feedback Tab */}
        {tab === 'feedback' && (
          <div data-testid="feedback-tab">
            {/* Shuffle Toggle */}
            <div className="bg-[#141414] border border-white/5 p-4 mb-4 flex items-center justify-between">
              <div>
                <p className="text-white text-sm font-sans font-medium">Shuffle Testimonials</p>
                <p className="text-white/40 text-xs font-sans mt-0.5">Randomize order on homepage (off = manual sort order)</p>
              </div>
              <Switch
                checked={settings.shuffle_testimonials ?? true}
                onCheckedChange={async (val) => {
                  const updated = { ...settings, shuffle_testimonials: val };
                  setSettings(updated);
                  await axios.put(`${API}/admin/settings`, updated, { headers: getAuthHeaders() });
                  toast.success(val ? 'Shuffle enabled' : 'Manual order active');
                }}
                data-testid="shuffle-testimonials-toggle"
              />
            </div>
            <div className="flex justify-between items-center mb-4">
              <p className="text-white/50 text-sm font-sans">
                {feedbackList.length} total &middot; {feedbackList.filter(f => f.is_approved).length} approved &middot; {feedbackList.filter(f => !f.is_approved).length} pending
              </p>
              <button
                onClick={() => { setEditingFeedback(null); setShowFeedbackModal(true); }}
                className="flex items-center gap-2 bg-[#D4AF37] text-black px-4 py-2 text-sm font-medium hover:bg-[#F3E5AB] transition-colors"
                data-testid="add-feedback-btn"
              >
                <Plus className="w-4 h-4" /> Add Feedback
              </button>
            </div>
            <div className="space-y-3">
              {feedbackList.map(fb => (
                <div key={fb.id} className={`bg-[#141414] border ${fb.is_approved ? 'border-white/10' : 'border-yellow-500/20'} p-4 flex gap-4`} data-testid={`feedback-item-${fb.id}`}>
                  {fb.image_url && (
                    <div className="w-16 h-16 flex-shrink-0 overflow-hidden">
                      <img src={fb.image_url} alt="" className="w-full h-full object-cover" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="text-white text-sm font-sans font-medium">{fb.customer_name}</p>
                      <span className={`text-[10px] uppercase px-1.5 py-0.5 font-sans ${fb.is_approved ? 'bg-green-500/10 text-green-400 border border-green-500/20' : 'bg-yellow-500/10 text-yellow-400 border border-yellow-500/20'}`}>
                        {fb.is_approved ? 'Approved' : 'Pending'}
                      </span>
                      <span className="text-[10px] uppercase text-white/30 font-sans">{fb.source}</span>
                    </div>
                    <div className="flex gap-0.5 mb-1">
                      {[1,2,3,4,5].map(i => (
                        <Star key={i} className={`w-3 h-3 ${i <= fb.rating ? 'text-[#D4AF37] fill-[#D4AF37]' : 'text-white/20'}`} />
                      ))}
                    </div>
                    <p className="text-white/60 text-sm font-sans truncate">{fb.comment}</p>
                  </div>
                  <div className="flex items-start gap-1.5 flex-shrink-0">
                    <button onClick={async () => {
                      try {
                        await axios.put(`${API}/admin/feedback/${fb.id}`, { is_approved: !fb.is_approved }, { headers: getAuthHeaders() });
                        toast.success(fb.is_approved ? 'Feedback hidden' : 'Feedback approved');
                        fetchData();
                      } catch { toast.error('Failed'); }
                    }} className={`p-1.5 transition-colors ${fb.is_approved ? 'text-green-400 hover:text-yellow-400' : 'text-yellow-400 hover:text-green-400'}`}
                      data-testid={`approve-feedback-${fb.id}`} title={fb.is_approved ? 'Unapprove' : 'Approve'}>
                      <Eye className="w-4 h-4" />
                    </button>
                    <button onClick={async () => {
                      const role = window.prompt('Team role for this person? (e.g., Ambassador, Founder, Core Team)', 'Ambassador');
                      if (role === null) return;
                      const remove = window.confirm('Also remove this entry from Feedback after promoting?');
                      try {
                        await axios.post(`${API}/admin/feedback/${fb.id}/promote-to-team`, { role: role || 'Ambassador', delete_feedback: remove }, { headers: getAuthHeaders() });
                        toast.success(`Promoted "${fb.customer_name}" to Team`);
                        fetchData();
                      } catch (e) { toast.error(e.response?.data?.detail || 'Promote failed'); }
                    }} className="p-1.5 text-white/40 hover:text-[#1C7D3F] transition-colors"
                      data-testid={`promote-team-${fb.id}`} title="Promote to Team">
                      <Users className="w-4 h-4" />
                    </button>
                    <button onClick={() => { setEditingFeedback(fb); setShowFeedbackModal(true); }}
                      className="p-1.5 text-white/40 hover:text-[#D4AF37] transition-colors" data-testid={`edit-feedback-${fb.id}`}>
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button onClick={async () => {
                      if (!window.confirm('Delete this feedback?')) return;
                      try {
                        await axios.delete(`${API}/admin/feedback/${fb.id}`, { headers: getAuthHeaders() });
                        toast.success('Deleted');
                        fetchData();
                      } catch { toast.error('Failed'); }
                    }} className="p-1.5 text-white/40 hover:text-red-400 transition-colors" data-testid={`delete-feedback-${fb.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
              {feedbackList.length === 0 && (
                <p className="text-white/30 text-sm text-center py-8 font-sans">No feedback yet</p>
              )}
            </div>
          </div>
        )}

        {/* Customers Tab */}
        {tab === 'customers' && (
          <div data-testid="customers-tab">
            <div className="flex flex-col sm:flex-row gap-3 mb-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                <input type="text" placeholder="Search customers..." value={customerSearch} onChange={e => setCustomerSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 bg-transparent border border-white/20 text-white text-sm focus:border-[#D4AF37] focus:outline-none placeholder:text-white/30" data-testid="customer-search-input" />
              </div>
              <p className="text-white/50 text-sm font-sans self-center whitespace-nowrap">{customers.length} customers</p>
            </div>
            {(() => {
              const filtered = customers.filter(c => {
                if (!customerSearch) return true;
                const s = customerSearch.toLowerCase();
                return c.email?.toLowerCase().includes(s) || c.name?.toLowerCase().includes(s) || c.phone?.includes(s);
              });
              const total = filtered.length;
              const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
              const paged = filtered.slice((customersPage - 1) * PAGE_SIZE, customersPage * PAGE_SIZE);
              return (
                <>
                  <div className="space-y-2">
                    {paged.map(c => (
                      <div key={c.email} className="bg-[#141414] border border-white/10 p-4 flex items-center gap-4" data-testid={`customer-row-${c.email}`}>
                        {c.picture ? (
                          <img src={c.picture} alt="" className="w-10 h-10 rounded-full flex-shrink-0" />
                        ) : (
                          <div className="w-10 h-10 bg-white/5 flex items-center justify-center flex-shrink-0 rounded-full">
                            <Users className="w-5 h-5 text-white/20" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-white text-sm font-sans font-medium">{c.name || 'No name'}</p>
                          <p className="text-white/40 text-xs font-sans">{c.email}</p>
                        </div>
                        <p className="text-white/30 text-xs font-sans hidden sm:block">{c.phone || 'No phone'}</p>
                        <span className="text-[10px] uppercase px-1.5 py-0.5 border font-sans hidden sm:block" style={{
                          borderColor: c.auth_provider === 'google' ? '#4285F4' : '#D4AF37',
                          color: c.auth_provider === 'google' ? '#4285F4' : '#D4AF37',
                        }}>{c.auth_provider || 'email'}</span>
                        <button onClick={() => { setEditingCustomer(c); setShowCustomerModal(true); }}
                          className="p-1.5 text-white/40 hover:text-[#D4AF37] transition-colors" data-testid={`edit-customer-${c.email}`}>
                          <Pencil className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                    {total === 0 && <p className="text-white/30 text-sm text-center py-8 font-sans">No customers found</p>}
                  </div>
                  <Pagination
                    page={customersPage}
                    pages={pages}
                    total={total}
                    limit={PAGE_SIZE}
                    onChange={setCustomersPage}
                    testIdPrefix="customers-pagination"
                  />
                </>
              );
            })()}
          </div>
        )}

        {/* Team Tab */}
        {tab === 'team' && (
          <div data-testid="team-tab">
            <div className="flex items-center justify-between mb-4">
              <p className="text-white/50 text-sm font-sans">{teamMembers.length} members</p>
              <button
                onClick={() => { setEditingTeamMember(null); setShowTeamModal(true); }}
                className="flex items-center gap-2 bg-[#1C7D3F] text-white px-4 py-2 text-sm hover:bg-[#2E9B50] transition-colors"
                data-testid="add-team-btn"
              >
                <Plus className="w-4 h-4" /> Add Member
              </button>
            </div>
            <div className="space-y-2">
              {teamMembers.map((m, idx) => (
                <div key={m.id} className="bg-[#141414] border border-white/10 p-4 flex items-center gap-4" data-testid={`team-row-${m.id}`}
                  draggable
                  onDragStart={e => e.dataTransfer.setData('text/plain', idx.toString())}
                  onDragOver={e => e.preventDefault()}
                  onDrop={async e => {
                    e.preventDefault();
                    const fromIdx = parseInt(e.dataTransfer.getData('text/plain'));
                    if (fromIdx === idx) return;
                    const reordered = [...teamMembers];
                    const [moved] = reordered.splice(fromIdx, 1);
                    reordered.splice(idx, 0, moved);
                    setTeamMembers(reordered);
                    // Update sort_order for all
                    for (let i = 0; i < reordered.length; i++) {
                      await axios.put(`${API}/admin/team/${reordered[i].id}`, { sort_order: i }, { headers: getAuthHeaders() });
                    }
                    toast.success('Order updated');
                  }}
                >
                  <GripVertical className="w-4 h-4 text-white/20 cursor-grab flex-shrink-0" />
                  {m.image_url ? (
                    <img src={m.image_url} alt="" className="w-12 h-12 rounded-full object-cover flex-shrink-0" />
                  ) : (
                    <div className="w-12 h-12 bg-white/5 rounded-full flex items-center justify-center flex-shrink-0">
                      <Users className="w-5 h-5 text-white/20" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm font-sans font-medium">{m.name}</p>
                    <p className="text-[#1C7D3F] text-xs font-sans">{m.role}</p>
                    {m.bio && <p className="text-white/30 text-xs font-sans truncate mt-0.5">{m.bio}</p>}
                  </div>
                  <span className={`text-[10px] uppercase px-1.5 py-0.5 border font-sans ${m.is_active ? 'border-green-500/40 text-green-400' : 'border-red-500/40 text-red-400'}`}>
                    {m.is_active ? 'Active' : 'Hidden'}
                  </span>
                  <button onClick={() => { setEditingTeamMember(m); setShowTeamModal(true); }}
                    className="p-1.5 text-white/40 hover:text-[#D4AF37] transition-colors" data-testid={`edit-team-${m.id}`}>
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button onClick={async () => {
                    if (!window.confirm(`Remove ${m.name}?`)) return;
                    await axios.delete(`${API}/admin/team/${m.id}`, { headers: getAuthHeaders() });
                    setTeamMembers(prev => prev.filter(x => x.id !== m.id));
                    toast.success('Removed');
                  }} className="p-1.5 text-white/40 hover:text-red-400 transition-colors" data-testid={`delete-team-${m.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
              {teamMembers.length === 0 && <p className="text-white/30 text-sm text-center py-8 font-sans">No team members yet. Add your first!</p>}
            </div>
          </div>
        )}



        {/* Custom Scent Tab */}
        {tab === 'custom_scent' && (
          <div data-testid="custom-scent-tab">
            {/* Sub-tabs */}
            <div className="flex gap-2 mb-4 border-b border-white/5 pb-3">
              {['orders', 'notes', 'presets'].map(st => (
                <button key={st} onClick={() => setCustomScentTab(st)}
                  className={`px-3 py-1.5 text-xs uppercase tracking-wider font-sans transition-colors ${
                    customScentTab === st ? 'text-[#D4AF37] border-b-2 border-[#D4AF37]' : 'text-white/40 hover:text-white/60'
                  }`} data-testid={`cs-subtab-${st}`}>
                  {st === 'orders' ? `Orders (${customScentOrders.length})` : st === 'notes' ? `Notes (${scentNotes.length})` : `Presets (${scentPresets.length})`}
                </button>
              ))}
            </div>

            {/* Custom Scent Orders */}
            {customScentTab === 'orders' && (
              <div className="space-y-2" data-testid="cs-orders-list">
                {customScentOrders.length === 0 && <p className="text-white/30 text-sm text-center py-8 font-sans">No custom scent orders yet</p>}
                {customScentOrders.map(order => (
                  <div key={order.id} className="bg-[#141414] border border-white/5">
                    <div className="flex items-center justify-between p-4 cursor-pointer hover:bg-white/5 transition-colors"
                      onClick={() => setExpandedCustomOrder(expandedCustomOrder === order.id ? null : order.id)} data-testid={`cs-order-${order.id}`}>
                      <div>
                        <p className="text-white text-sm font-mono">{order.order_number}</p>
                        <p className="text-white/40 text-xs">{order.customer_name} — <span className="text-[#D4AF37]">{order.scent_name}</span></p>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className={`text-xs px-2 py-1 font-sans ${getStatusColor(order.status)}`}>
                          {formatStatusLabel(order.status)}
                        </span>
                        <span className="text-[#D4AF37] text-sm font-sans">₱{order.price?.toLocaleString()}</span>
                        {expandedCustomOrder === order.id ? <ChevronUp className="w-4 h-4 text-white/40" /> : <ChevronDown className="w-4 h-4 text-white/40" />}
                      </div>
                    </div>
                    {expandedCustomOrder === order.id && (
                      <div className="border-t border-white/5 p-4 space-y-3" data-testid={`cs-order-details-${order.id}`}>
                        <div className="grid grid-cols-2 gap-3 text-sm">
                          <div><span className="text-white/30 text-xs block">Vibe</span><span className="text-white/70">{order.vibe}</span></div>
                          <div><span className="text-white/30 text-xs block">Size</span><span className="text-white/70">{order.size?.size} {order.size?.type}</span></div>
                          <div><span className="text-white/30 text-xs block">Main Notes</span><span className="text-white/70">{order.main_notes?.join(', ')}</span></div>
                          <div><span className="text-white/30 text-xs block">Supporting</span><span className="text-white/70">{order.supporting_notes?.join(', ')}</span></div>
                          <div><span className="text-white/30 text-xs block">Phone</span><span className="text-white/70">{order.phone}</span></div>
                          <div><span className="text-white/30 text-xs block">Email</span><span className="text-white/70">{order.customer_email || '—'}</span></div>
                        </div>
                        {order.ai_description && (
                          <div className="bg-black/30 border border-white/5 p-3">
                            <p className="text-[10px] uppercase text-[#D4AF37] tracking-wider mb-1">AI Description</p>
                            <p className="text-white/50 text-sm italic">{order.ai_description}</p>
                          </div>
                        )}
                        {order.notes_to_maker && (
                          <div><p className="text-[10px] uppercase text-white/30 tracking-wider">Customer Notes</p><p className="text-white/60 text-sm">{order.notes_to_maker}</p></div>
                        )}
                        {/* Admin notes */}
                        <div>
                          <p className="text-[10px] uppercase text-white/30 tracking-wider mb-1">Admin Notes</p>
                          <textarea
                            defaultValue={order.admin_notes || ''}
                            onBlur={async (e) => {
                              if (e.target.value !== (order.admin_notes || '')) {
                                try {
                                  await axios.put(`${API}/admin/custom-scent/orders/${order.id}`, { admin_notes: e.target.value }, { headers: getAuthHeaders() });
                                  toast.success('Notes saved');
                                } catch { toast.error('Failed'); }
                              }
                            }}
                            className="w-full bg-transparent border border-white/10 text-white text-sm p-2 focus:border-[#D4AF37] focus:outline-none resize-none h-16 placeholder:text-white/15"
                            placeholder="Add internal notes..."
                            data-testid={`cs-admin-notes-${order.id}`}
                          />
                        </div>
                        {/* Status actions */}
                        <div>
                          <p className="text-[10px] uppercase text-white/30 tracking-wider mb-2">Update Status</p>
                          <div className="flex flex-wrap gap-2">
                            {['Pending', 'In Progress', 'Completed', 'Rejected'].map(s => (
                              <button key={s} onClick={async () => {
                                try {
                                  await axios.put(`${API}/admin/custom-scent/orders/${order.id}`, { status: s }, { headers: getAuthHeaders() });
                                  toast.success(`Status: ${s}`);
                                  fetchData();
                                } catch { toast.error('Failed'); }
                              }} disabled={order.status === s}
                                className={`text-xs px-3 py-1.5 transition-colors ${order.status === s ? 'bg-[#D4AF37] text-black font-medium' : 'border border-white/20 text-white/50 hover:border-white/40'}`}
                                data-testid={`cs-status-${s.toLowerCase().replace(' ', '-')}-${order.id}`}>
                                {s}
                              </button>
                            ))}
                          </div>
                        </div>
                        <p className="text-white/20 text-xs font-sans">Created: {new Date(order.created_at).toLocaleString()}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Scent Notes Management */}
            {customScentTab === 'notes' && (
              <div data-testid="cs-notes-list">
                <div className="flex justify-between items-center mb-4">
                  <p className="text-white/40 text-sm">{scentNotes.length} notes</p>
                  <button onClick={() => { setEditingScentNote(null); setShowScentNoteModal(true); }}
                    className="flex items-center gap-2 bg-[#D4AF37] text-black px-4 py-2 text-sm font-medium hover:bg-[#F3E5AB] transition-colors" data-testid="add-note-btn">
                    <Plus className="w-4 h-4" /> Add Note
                  </button>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-white/10">
                        <th className="text-left text-xs uppercase tracking-wider text-white/40 font-sans py-2 px-2">Name</th>
                        <th className="text-left text-xs uppercase tracking-wider text-white/40 font-sans py-2 px-2">Category</th>
                        <th className="text-left text-xs uppercase tracking-wider text-white/40 font-sans py-2 px-2">Family</th>
                        <th className="text-left text-xs uppercase tracking-wider text-white/40 font-sans py-2 px-2">Compatible</th>
                        <th className="text-right text-xs uppercase tracking-wider text-white/40 font-sans py-2 px-2">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {scentNotes.map(note => (
                        <tr key={note.id} className="border-b border-white/5 hover:bg-white/5">
                          <td className="py-2 px-2 text-white text-sm">{note.name}</td>
                          <td className="py-2 px-2 text-white/50 text-xs uppercase">{note.category}</td>
                          <td className="py-2 px-2 text-white/50 text-xs">{note.family}</td>
                          <td className="py-2 px-2 text-white/30 text-xs truncate max-w-[200px]">{note.good_with?.join(', ')}</td>
                          <td className="py-2 px-2 text-right">
                            <div className="flex items-center justify-end gap-1">
                              <button onClick={() => { setEditingScentNote(note); setShowScentNoteModal(true); }}
                                className="p-1 text-white/40 hover:text-[#D4AF37]" data-testid={`edit-note-${note.id}`}><Pencil className="w-3.5 h-3.5" /></button>
                              <button onClick={async () => {
                                if (!window.confirm('Delete this note?')) return;
                                try { await axios.delete(`${API}/admin/scent-notes/${note.id}`, { headers: getAuthHeaders() }); toast.success('Deleted'); fetchData(); } catch { toast.error('Failed'); }
                              }} className="p-1 text-white/40 hover:text-red-400" data-testid={`delete-note-${note.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Scent Presets Management */}
            {customScentTab === 'presets' && (
              <div data-testid="cs-presets-list">
                <div className="flex justify-between items-center mb-4">
                  <p className="text-white/40 text-sm">{scentPresets.length} presets</p>
                  <button onClick={() => { setEditingScentPreset(null); setShowScentPresetModal(true); }}
                    className="flex items-center gap-2 bg-[#D4AF37] text-black px-4 py-2 text-sm font-medium hover:bg-[#F3E5AB] transition-colors" data-testid="add-preset-btn">
                    <Plus className="w-4 h-4" /> Add Preset
                  </button>
                </div>
                <div className="space-y-2">
                  {scentPresets.map(preset => (
                    <div key={preset.id} className="bg-[#141414] border border-white/10 p-4 flex items-center gap-4" data-testid={`preset-row-${preset.id}`}>
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm font-medium">{preset.name}</p>
                        <p className="text-white/30 text-xs">{preset.vibe} — Main: {preset.main_note} — Support: {preset.supporting_notes?.join(', ')}</p>
                      </div>
                      <span className={`text-[10px] uppercase px-1.5 py-0.5 border font-sans ${preset.is_active ? 'border-green-500/20 text-green-400' : 'border-red-500/20 text-red-400'}`}>
                        {preset.is_active ? 'Active' : 'Hidden'}
                      </span>
                      <div className="flex gap-1">
                        <button onClick={() => { setEditingScentPreset(preset); setShowScentPresetModal(true); }}
                          className="p-1 text-white/40 hover:text-[#D4AF37]" data-testid={`edit-preset-${preset.id}`}><Pencil className="w-3.5 h-3.5" /></button>
                        <button onClick={async () => {
                          if (!window.confirm('Delete this preset?')) return;
                          try { await axios.delete(`${API}/admin/scent-presets/${preset.id}`, { headers: getAuthHeaders() }); toast.success('Deleted'); fetchData(); } catch { toast.error('Failed'); }
                        }} className="p-1 text-white/40 hover:text-red-400" data-testid={`delete-preset-${preset.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                      </div>
                    </div>
                  ))}
                  {scentPresets.length === 0 && <p className="text-white/30 text-sm text-center py-8 font-sans">No presets yet</p>}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Social/SEO Tab */}
        {tab === 'og_meta' && (
          <div data-testid="og-meta-tab" className="space-y-6">
            <h3 className="text-lg text-white font-sans mb-1">Default Share Preview</h3>
            <p className="text-white/40 text-xs font-sans mb-2">Controls how every page on your site appears when shared on Facebook, Messenger, WhatsApp, and Instagram.</p>
            <div className="bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-sm px-3 py-2 mb-4">
              <p className="text-[#F3E5AB] text-[11px] font-sans leading-relaxed">
                <strong>Important:</strong> Due to platform-level security policies, Facebook / WhatsApp / Messenger / Instagram cannot fetch product-specific images for previews. They will display these default settings for ALL shared links. <strong>Twitter/X, Discord, iMessage, and Telegram</strong> will still show product-specific previews automatically.
              </p>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-white/60 text-xs uppercase tracking-wider font-sans block mb-1">Title</label>
                <input
                  type="text"
                  value={settings.og_meta?.title || ''}
                  onChange={e => setSettings(s => ({ ...s, og_meta: { ...(s.og_meta || {}), title: e.target.value } }))}
                  className="w-full bg-transparent border border-white/20 text-white text-sm px-3 py-2 focus:border-[#D4AF37] focus:outline-none"
                  data-testid="og-title-input"
                />
              </div>
              <div>
                <label className="text-white/60 text-xs uppercase tracking-wider font-sans block mb-1">Description</label>
                <textarea
                  value={settings.og_meta?.description || ''}
                  onChange={e => setSettings(s => ({ ...s, og_meta: { ...(s.og_meta || {}), description: e.target.value } }))}
                  rows={3}
                  className="w-full bg-transparent border border-white/20 text-white text-sm px-3 py-2 focus:border-[#D4AF37] focus:outline-none resize-none"
                  data-testid="og-description-input"
                />
              </div>
              <div>
                <label className="text-white/60 text-xs uppercase tracking-wider font-sans block mb-1">Share Image</label>
                {/* Drag & Drop Upload Zone */}
                <div
                  className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors cursor-pointer ${
                    ogDragging ? 'border-[#D4AF37] bg-[#D4AF37]/5' : 'border-white/20 hover:border-white/40'
                  }`}
                  data-testid="og-image-dropzone"
                  onDragOver={e => { e.preventDefault(); setOgDragging(true); }}
                  onDragLeave={e => { e.preventDefault(); setOgDragging(false); }}
                  onDrop={async e => {
                    e.preventDefault();
                    setOgDragging(false);
                    const file = e.dataTransfer.files[0];
                    if (!file) return;
                    if (!file.type.startsWith('image/')) { toast.error('Only image files allowed'); return; }
                    setOgUploading(true);
                    try {
                      const fd = new FormData();
                      fd.append('file', file);
                      const res = await axios.post(`${API}/upload/share-image`, fd);
                      const url = `${window.location.origin}/api/files/${res.data.storage_path}`;
                      setSettings(s => ({ ...s, og_meta: { ...(s.og_meta || {}), image: url } }));
                      toast.success('Image uploaded!');
                    } catch (err) {
                      toast.error('Upload failed');
                    }
                    setOgUploading(false);
                  }}
                  onClick={() => document.getElementById('og-image-file-input').click()}
                >
                  <input
                    id="og-image-file-input"
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={async e => {
                      const file = e.target.files[0];
                      if (!file) return;
                      setOgUploading(true);
                      try {
                        const fd = new FormData();
                        fd.append('file', file);
                        const res = await axios.post(`${API}/upload/share-image`, fd);
                        const url = `${window.location.origin}/api/files/${res.data.storage_path}`;
                        setSettings(s => ({ ...s, og_meta: { ...(s.og_meta || {}), image: url } }));
                        toast.success('Image uploaded!');
                      } catch (err) {
                        toast.error('Upload failed');
                      }
                      setOgUploading(false);
                      e.target.value = '';
                    }}
                  />
                  {ogUploading ? (
                    <div className="flex flex-col items-center gap-2">
                      <Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" />
                      <p className="text-white/50 text-sm font-sans">Uploading...</p>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-2">
                      <Upload className="w-8 h-8 text-white/30" />
                      <p className="text-white/50 text-sm font-sans">Drag & drop an image here, or click to browse</p>
                      <p className="text-white/30 text-xs font-sans">JPG, PNG, WebP (Max 5MB)</p>
                    </div>
                  )}
                </div>
                {settings.og_meta?.image && (
                  <div className="mt-3 flex items-start gap-3">
                    <div className="w-48 h-24 bg-[#141414] overflow-hidden flex-shrink-0 border border-white/10">
                      <img src={settings.og_meta.image} alt="Preview" className="w-full h-full object-cover" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white/40 text-xs font-sans truncate mb-1">{settings.og_meta.image}</p>
                      <button
                        onClick={() => setSettings(s => ({ ...s, og_meta: { ...(s.og_meta || {}), image: '' } }))}
                        className="text-red-400/60 hover:text-red-400 text-xs font-sans"
                        data-testid="og-image-remove"
                      >
                        Remove Image
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="border-t border-white/5 pt-6">
              <h3 className="text-lg text-white font-sans mb-4">Trust Stats (Homepage)</h3>
              <p className="text-white/40 text-xs font-sans mb-4">Social proof numbers shown on the homepage.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-white/60 text-xs uppercase tracking-wider font-sans block mb-1">Bottles Sold</label>
                  <input
                    type="text"
                    value={settings.trust_stats?.bottles_sold || ''}
                    onChange={e => setSettings(s => ({ ...s, trust_stats: { ...(s.trust_stats || {}), bottles_sold: e.target.value } }))}
                    className="w-full bg-transparent border border-white/20 text-white text-sm px-3 py-2 focus:border-[#D4AF37] focus:outline-none"
                    data-testid="trust-bottles-input"
                  />
                </div>
                <div>
                  <label className="text-white/60 text-xs uppercase tracking-wider font-sans block mb-1">Happy Customers</label>
                  <input
                    type="text"
                    value={settings.trust_stats?.happy_customers || ''}
                    onChange={e => setSettings(s => ({ ...s, trust_stats: { ...(s.trust_stats || {}), happy_customers: e.target.value } }))}
                    className="w-full bg-transparent border border-white/20 text-white text-sm px-3 py-2 focus:border-[#D4AF37] focus:outline-none"
                    data-testid="trust-customers-input"
                  />
                </div>
                <div>
                  <label className="text-white/60 text-xs uppercase tracking-wider font-sans block mb-1">Concentration Level</label>
                  <input
                    type="text"
                    value={settings.trust_stats?.concentration || ''}
                    onChange={e => setSettings(s => ({ ...s, trust_stats: { ...(s.trust_stats || {}), concentration: e.target.value } }))}
                    className="w-full bg-transparent border border-white/20 text-white text-sm px-3 py-2 focus:border-[#D4AF37] focus:outline-none"
                    data-testid="trust-concentration-input"
                  />
                </div>
                <div>
                  <label className="text-white/60 text-xs uppercase tracking-wider font-sans block mb-1">Longevity Claim</label>
                  <input
                    type="text"
                    value={settings.trust_stats?.longevity_claim || ''}
                    onChange={e => setSettings(s => ({ ...s, trust_stats: { ...(s.trust_stats || {}), longevity_claim: e.target.value } }))}
                    className="w-full bg-transparent border border-white/20 text-white text-sm px-3 py-2 focus:border-[#D4AF37] focus:outline-none"
                    data-testid="trust-longevity-input"
                  />
                </div>
              </div>
            </div>

            <button
              onClick={async () => {
                try {
                  await axios.put(`${API}/admin/settings`, settings, { headers: getAuthHeaders() });
                  toast.success('Social & Trust settings saved');
                } catch (e) {
                  toast.error('Save failed');
                }
              }}
              className="bg-[#D4AF37] text-black px-6 py-2.5 text-sm uppercase tracking-[0.15em] font-medium hover:bg-[#F3E5AB] transition-colors"
              data-testid="save-og-settings-btn"
            >
              Save Changes
            </button>
          </div>
        )}
      </div>
        </div>
      </div>

      {/* Product Modal */}
      {showProductModal && (
        <ProductModal
          product={editingProduct}
          onClose={() => setShowProductModal(false)}
          onSave={() => { setShowProductModal(false); fetchData(); }}
        />
      )}

      {/* Collection Modal */}
      {showCollectionModal && (
        <CollectionModal
          collection={editingCollection}
          onClose={() => setShowCollectionModal(false)}
          onSave={() => { setShowCollectionModal(false); fetchData(); }}
        />
      )}

      {/* Feedback Modal */}
      {showFeedbackModal && (
        <FeedbackModal
          feedback={editingFeedback}
          onClose={() => setShowFeedbackModal(false)}
          onSave={() => { setShowFeedbackModal(false); fetchData(); }}
        />
      )}

      {/* Customer Modal */}
      {showCustomerModal && (
        <CustomerModal
          customer={editingCustomer}
          onClose={() => setShowCustomerModal(false)}
          onSave={() => { setShowCustomerModal(false); fetchData(); }}
        />
      )}

      {/* Team Modal */}
      {showTeamModal && (
        <TeamMemberModal
          member={editingTeamMember}
          onClose={() => setShowTeamModal(false)}
          onSave={() => { setShowTeamModal(false); fetchData(); }}
        />
      )}


      {/* Scent Note Modal */}
      {showScentNoteModal && (
        <ScentNoteModal
          note={editingScentNote}
          allNotes={scentNotes}
          onClose={() => setShowScentNoteModal(false)}
          onSave={() => { setShowScentNoteModal(false); fetchData(); }}
        />
      )}

      {/* Scent Preset Modal */}
      {showScentPresetModal && (
        <ScentPresetModal
          preset={editingScentPreset}
          notes={scentNotes}
          onClose={() => setShowScentPresetModal(false)}
          onSave={() => { setShowScentPresetModal(false); fetchData(); }}
        />
      )}
    </div>
  );
}
